import React, { useState, useEffect } from "react";
import RegisterImg from "assets/register_assets/register_img.png";
import { useLocation, useNavigate } from "react-router-dom";
import goBootpayRequest, { dismissBootpay } from "components/bootpay";
import { login, register } from "firebase_/firebase_function/firebase_user";
import OnlyConfirmDialog from "components/onlyConfirm_dialog";
import Footer from "components/footer/footer";
import ConfirmDialog from "components/confirm_dialog";
import ExpertCategoryDialog from "components/register/select_category";
import MobileFooter from "components/footer/footer_m";
import { useUser } from "util/context/user_context";
import OnlyLoadingScreen from "components/loading_screen";

const C = {
  ink: "#111827",
  body: "#374151",
  sub: "#6B7280",
  line: "#E5E7EB",
  greenDark: "#047857",
  greenBg: "#ECFDF5",
  greenBorder: "#A7F3D0",
  green: "#059669",
  bg: "#F7FAF7",
  amber800: "#92400E",
};

const FONT =
  'Pretendard, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, sans-serif';

function Section({ label, hint, children }) {
  return (
    <div
      style={{
        background: "#fff",
        border: `1px solid ${C.line}`,
        borderRadius: 14,
        padding: 18,
        display: "flex",
        flexDirection: "column",
        gap: 12,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <span
          style={{
            fontSize: 13,
            color: C.ink,
            fontWeight: 700,
            letterSpacing: "-0.01em",
          }}
        >
          {label}
        </span>
        {hint && (
          <span style={{ fontSize: 11, color: C.sub, fontWeight: 600 }}>
            {hint}
          </span>
        )}
      </div>
      {children}
    </div>
  );
}

const inputStyle = {
  width: "100%",
  height: 50,
  padding: "0 14px",
  borderRadius: 10,
  border: `1px solid ${C.line}`,
  background: "#fff",
  fontSize: 15,
  color: C.ink,
  outline: "none",
  fontFamily: "inherit",
  boxSizing: "border-box",
};

const ctaBtnStyle = {
  width: "100%",
  height: 50,
  padding: "0 14px",
  borderRadius: 10,
  border: `1px solid ${C.greenBorder}`,
  background: C.greenBg,
  color: C.greenDark,
  fontSize: 15,
  fontWeight: 700,
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 8,
  letterSpacing: "-0.01em",
};

const ghostSmStyle = {
  height: 32,
  padding: "0 12px",
  borderRadius: 8,
  border: `1px solid ${C.line}`,
  background: "#fff",
  color: C.body,
  fontSize: 12,
  fontWeight: 700,
  cursor: "pointer",
  whiteSpace: "nowrap",
};

function ConfirmedCard({ children, onEdit }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 10,
        padding: "12px 14px",
        borderRadius: 10,
        background: C.greenBg,
        border: `1px solid ${C.greenBorder}`,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          minWidth: 0,
          flex: 1,
        }}
      >
        <span
          style={{ color: C.greenDark, fontWeight: 800, fontSize: 14 }}
          aria-hidden
        >
          ✓
        </span>
        <span
          style={{
            fontSize: 14,
            color: C.ink,
            fontWeight: 600,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {children}
        </span>
      </div>
      {onEdit && (
        <button type="button" onClick={onEdit} style={ghostSmStyle}>
          변경
        </button>
      )}
    </div>
  );
}

function UserTypePicker({ value, onPick }) {
  const opts = [
    { key: "농가", icon: "🌾" },
    { key: "수리기사", icon: "🔧" },
  ];
  return (
    <div style={{ display: "flex", gap: 10 }}>
      {opts.map((o) => {
        const active = value === o.key;
        return (
          <button
            key={o.key}
            type="button"
            onClick={() => onPick(o.key)}
            style={{
              flex: 1,
              height: 60,
              borderRadius: 12,
              border: `2px solid ${active ? C.greenDark : C.line}`,
              background: active ? C.greenBg : "#fff",
              color: active ? C.greenDark : C.body,
              fontSize: 16,
              fontWeight: active ? 800 : 600,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
              letterSpacing: "-0.01em",
              transition: "all 0.15s ease",
            }}
          >
            <span style={{ fontSize: 20 }} aria-hidden>
              {o.icon}
            </span>
            {o.key}
          </button>
        );
      })}
    </div>
  );
}

function RegisterScreen({ isMobile }) {
  const navigate = useNavigate();
  const location = useLocation();
  const userId = location.state?.userId;
  const [selectedOption, setSelectedOption] = useState(null);
  const [address, setAddress] = useState("");
  const [addressDetail, setAddressDetail] = useState("");
  const [material, setMaterial] = useState("");
  const [affiliation, setAffiliation] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [modalMessage, setModalMessage] = useState("");
  const [modalTitle, setModalTitle] = useState("");
  const [modalItems, setModalItems] = useState([]);
  const [onlyConfirmModal, setOnlyConfirmModal] = useState(false);
  const [confirmModal, setConfirmModal] = useState(false);
  const [openCategory, setOpenCategory] = useState(false);
  const [loading, setLoading] = useState(false);
  const [authBusy, setAuthBusy] = useState(false); // 부트페이 본인인증 진행 중
  const { setUserData } = useUser();

  /// 회원가입 버튼 — 누락 항목 식별해서 사용자에게 명시
  const goRegister = async () => {
    const missing = [];
    if (!selectedOption || !String(selectedOption).trim()) {
      missing.push("사용자 유형");
    }
    if (!name || !phone) {
      missing.push("본인인증 (이름·휴대폰)");
    }
    if (!address || !String(address).trim()) {
      missing.push("주소");
    }
    if (!addressDetail || !String(addressDetail).trim()) {
      missing.push("상세주소");
    }
    if (selectedOption && (!material || !String(material).trim())) {
      missing.push(selectedOption === "수리기사" ? "전문 분야" : "주요 작물 / 축산");
    }
    if (!userId) {
      missing.push("로그인 정보 (다시 로그인이 필요해요)");
    }
    if (missing.length > 0) {
      setModalTitle("입력하지 않은 항목이 있어요");
      setModalMessage("아래 항목을 입력하신 뒤 다시 시도해주세요.");
      setModalItems(missing);
      setOnlyConfirmModal(true);
      return;
    }
    setConfirmModal(true);
  };

  /// 회원가입 진행
  const startRegister = async () => {
    setLoading(true);
    try {
      const body = {
        address,
        addressDetail,
        material,
        // 소속은 선택사항 — 빈 값 검증에서 제외하고 제출 시에만 포함
        affiliation,
        name,
        phone,
        userType: selectedOption,
        id: userId,
      };
      const response = await register(body);
      if (response === "success") {
        const data = await login(userId);
        await setUserData(data[0]);
        selectedOption === "수리기사"
          ? navigate("/expertmain")
          : navigate("/main");
      }
    } catch (error) {
      console.error("startRegister Error : ", error);
    } finally {
      setLoading(false);
    }
  };

  /// 부트페이 콜백 (성공/취소 어느 경로든 setAuthBusy 해제)
  const handleBootpaySuccess = (data) => {
    setPhone(data["phone"]);
    setName(data["name"]);
    setAuthBusy(false);
  };

  const startBootpayAuth = async () => {
    setAuthBusy(true);
    try {
      await goBootpayRequest(handleBootpaySuccess);
    } catch {}
    finally {
      // 성공 콜백에서도 setAuthBusy(false) 이지만, 취소/에러로 콜백 안 오면 여기서 보장
      setAuthBusy(false);
    }
  };

  const cancelBootpayAuth = () => {
    dismissBootpay();
    setAuthBusy(false);
  };

  /// 사용자 유형 선택
  const handlePickUserType = (opt) => {
    if (opt === "수리기사") {
      setOpenCategory(true);
      return;
    }
    setMaterial("");
    setSelectedOption(opt);
  };

  /// 수리기사 카테고리 선택
  const closeCategory = (tag) => {
    setMaterial(tag);
    setSelectedOption("수리기사");
  };

  /// 주소검색(카카오)
  const handleAddressSearch = () => {
    try {
      const popup = window.open(
        "http://ddukddaki.com:3989/api/get/kopo",
        "주소검색",
        "width=500,height=600",
      );

      const messageHandler = (event) => {
        try {
          const data =
            typeof event.data === "string"
              ? JSON.parse(event.data)
              : event.data;
          if (data.address) {
            setAddress(data.address);
            if (popup && !popup.closed) popup.close();
          }
        } catch (error) {
          console.error("주소 데이터 파싱 오류:", error);
        }
      };

      window.addEventListener("message", messageHandler);

      const popupCheckInterval = setInterval(() => {
        if (popup.closed) {
          clearInterval(popupCheckInterval);
          window.removeEventListener("message", messageHandler);
        }
      }, 500);
    } catch (error) {
      console.error("주소검색 에러 : ", error);
    }
  };

  /// 카카오 주소검색(외부 CustomEvent 처리)
  useEffect(() => {
    const handleKakaoAddress = (event) => {
      try {
        const addressData =
          typeof event.detail === "string"
            ? JSON.parse(event.detail)
            : event.detail;
        if (addressData.address) setAddress(addressData.address);
      } catch (e) {
        console.error("CustomEvent 주소 처리 오류:", e);
      }
    };
    window.addEventListener("FromKakaoAddressSearch", handleKakaoAddress);
    return () => {
      window.removeEventListener("FromKakaoAddressSearch", handleKakaoAddress);
    };
  }, []);

  const formatPhone = (p) =>
    p ? p.replace(/^(\d{3})(\d{3,4})(\d{4})$/, "$1-$2-$3") : "";

  const isExpert = selectedOption === "수리기사";

  return (
    <>
      <div
        style={{
          fontFamily: FONT,
          display: "flex",
          width: "100%",
          minHeight: "100vh",
          background: C.bg,
        }}
      >
        {!isMobile && (
          <div
            style={{
              flex: 1,
              minWidth: 0,
              alignSelf: "stretch",
              overflow: "hidden",
            }}
          >
            <img
              src={RegisterImg}
              alt=""
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                display: "block",
              }}
            />
          </div>
        )}

        <div
          style={{
            flex: 1,
            minWidth: !isMobile ? 520 : undefined,
            display: "flex",
            justifyContent: "center",
            alignItems: "flex-start",
            padding: isMobile ? "24px 16px 40px" : "48px 32px",
            overflow: "auto",
          }}
        >
          <div
            style={{
              width: "100%",
              maxWidth: 480,
              display: "flex",
              flexDirection: "column",
              gap: 14,
            }}
          >
            {/* 뒤로가기 */}
            <button
              type="button"
              onClick={() => navigate("/")}
              style={{
                alignSelf: "flex-start",
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                marginBottom: 4,
                padding: "8px 14px",
                borderRadius: 9999,
                border: `1px solid ${C.line}`,
                background: "#fff",
                color: C.body,
                fontSize: 13,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              ← 뒤로
            </button>

            {/* 타이틀 */}
            <div style={{ marginBottom: 4 }}>
              <div
                style={{
                  fontSize: 26,
                  fontWeight: 800,
                  color: C.ink,
                  letterSpacing: "-0.02em",
                }}
              >
                회원가입
              </div>
              <div style={{ marginTop: 4, fontSize: 13, color: C.sub }}>
                아래 항목을 순서대로 입력해주세요.
              </div>
            </div>

            {/* 1. 사용자 유형 (맨 위로 이동) */}
            <Section label="사용자 유형">
              <UserTypePicker
                value={selectedOption}
                onPick={handlePickUserType}
              />
            </Section>

            {/* 2. 본인인증 (이름 + 휴대폰) */}
            <Section
              label="본인인증"
              hint={phone && name ? "완료" : "필수"}
            >
              {phone && name ? (
                <ConfirmedCard onEdit={startBootpayAuth}>
                  {name} · {formatPhone(phone)}
                </ConfirmedCard>
              ) : (
                <button
                  type="button"
                  onClick={startBootpayAuth}
                  style={ctaBtnStyle}
                >
                  본인인증하기
                </button>
              )}
            </Section>

            {/* 3. 주소 */}
            <Section label="주소">
              {address ? (
                <ConfirmedCard onEdit={handleAddressSearch}>
                  {address}
                </ConfirmedCard>
              ) : (
                <button
                  type="button"
                  onClick={handleAddressSearch}
                  style={ctaBtnStyle}
                >
                  주소 검색하기
                </button>
              )}
              <input
                type="text"
                placeholder="상세주소를 입력해주세요"
                value={addressDetail}
                onChange={(e) => setAddressDetail(e.target.value)}
                style={inputStyle}
              />
            </Section>

            {/* 4. 주요작물 / 전문분야 (사용자 유형 선택 후만 노출) */}
            {selectedOption &&
              (isExpert ? (
                <Section label="전문 분야">
                  {material ? (
                    <ConfirmedCard onEdit={() => setOpenCategory(true)}>
                      {material}
                    </ConfirmedCard>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setOpenCategory(true)}
                      style={ctaBtnStyle}
                    >
                      전문 분야 선택하기
                    </button>
                  )}
                </Section>
              ) : (
                <Section label="주요 작물 / 축산">
                  <input
                    type="text"
                    placeholder="예: 벼, 토마토, 한우"
                    value={material}
                    onChange={(e) => setMaterial(e.target.value)}
                    style={inputStyle}
                  />
                </Section>
              ))}

            {/* 5. 소속 (선택) */}
            <Section label="소속" hint="선택">
              <input
                type="text"
                placeholder="소속을 입력해주세요 (예: ○○농기계센터)"
                value={affiliation}
                onChange={(e) => setAffiliation(e.target.value)}
                style={inputStyle}
              />
            </Section>

            {/* 6. 가입하기 */}
            <button
              type="button"
              onClick={goRegister}
              style={{
                width: "100%",
                height: 54,
                borderRadius: 12,
                border: "none",
                background: C.greenDark,
                color: "#fff",
                fontSize: 16,
                fontWeight: 800,
                cursor: "pointer",
                letterSpacing: "-0.01em",
                marginTop: 4,
              }}
            >
              가입하기
            </button>
          </div>
        </div>

        <OnlyConfirmDialog
          open={onlyConfirmModal}
          onClose={() => {
            setOnlyConfirmModal(false);
            setModalItems([]);
            setModalTitle("");
          }}
          title={modalTitle}
          message={modalMessage}
          items={modalItems}
        />
        <ConfirmDialog
          open={confirmModal}
          onClose={() => setConfirmModal(false)}
          onConfirm={() => {
            startRegister();
            setConfirmModal(false);
          }}
          message={"회원가입을 진행 하시겠습니까?"}
        />
        <ExpertCategoryDialog
          open={openCategory}
          setIsDialogOpen={setOpenCategory}
          onClose={closeCategory}
        />
      </div>
      {/* Footer 의 기본 marginTop(데스크톱 60 / 모바일 48)을 상쇄해 이미지/콘텐츠가 푸터에 바로 닿도록 */}
      <div style={{ marginTop: isMobile ? -48 : -60 }}>
        {isMobile ? <MobileFooter /> : <Footer />}
      </div>
      <OnlyLoadingScreen isOpen={loading} />

      {/* 부트페이 본인인증 취소 오버레이 (인증 창 밖을 클릭해도 안 닫히는 문제 대응) */}
      {authBusy && (
        <button
          type="button"
          onClick={cancelBootpayAuth}
          style={{
            position: "fixed",
            top: 16,
            right: 16,
            zIndex: 2147483647, // max int — Bootpay iframe 위로
            padding: "10px 16px",
            borderRadius: 9999,
            border: "1px solid #FECACA",
            background: "rgba(0,0,0,0.78)",
            color: "#fff",
            fontSize: 13,
            fontWeight: 800,
            cursor: "pointer",
            boxShadow: "0 8px 24px rgba(0,0,0,0.35)",
          }}
        >
          ✕ 본인인증 취소
        </button>
      )}
    </>
  );
}

export default RegisterScreen;
